import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";


import { FileDownIcon, WalletIcon } from "lucide-react";
import { useMemo, useState } from "react";
import type { WalletTransactionRow } from "../../domain/repo/get-wallet-transactions";
import type { WalletParams, WalletTransactionsParams } from "../api/client";
import { reportsApi } from "../api/client";
import {
  useWalletSummaryQuery,
  useWalletTransactionsQuery,
} from "../api/queries";
import { WalletReportsFilter } from "../ui/wallet/WalletReportsFilter";
import { WalletReportsSummaryCards } from "../ui/wallet/WalletReportsSummaryCards";
import { WalletReportsTable } from "../ui/wallet/WalletReportsTable";

function exportWalletToCsv(transactions: WalletTransactionRow[]) {
  const getTypeLabel = (type: string) => {
    switch (type) {
      case "TOPUP":
        return "ເຕີມເງິນ";
      case "PAYMENT":
        return "ຈ່າຍ";
      case "REFUND":
        return "ຄືນເງິນ";
      case "ADJUSTMENT":
        return "ປັບປຸງ";
      default:
        return type;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "PENDING":
        return "ລໍຖ້າ";
      case "APPROVED":
        return "ອະນຸມັດ";
      case "POSTED":
        return "ບັນທຶກແລ້ວ";
      case "REJECTED":
        return "ປະຕິເສດ";
      default:
        return status;
    }
  };

  const headers = [
    "ວັນທີ",
    "ປະເພດ",
    "ຈຳນວນເງິນ (LAK)",
    "ລູກຄ້າ",
    "ເບີໂທ",
    "ສະຖານະ",
    "ອໍເດີ",
  ];

  const rows = transactions.map((item) => {
    // Handle createdAt - API returns string, convert to Date then format
    const createdAtDate =
      item.createdAt instanceof Date
        ? item.createdAt
        : new Date(item.createdAt);
    const createdAtStr = createdAtDate.toISOString();

    return [
      createdAtStr,
      getTypeLabel(item.type),
      String(item.amountLak),
      item.customerName || "",
      item.customerPhone || "",
      getStatusLabel(item.status),
      item.refOrderId || "",
    ];
  });

  const csvContent = [
    "=== ທຸລະກຳ Wallet ===",
    headers.join(","),
    ...rows.map((row) =>
      row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","),
    ),
  ].join("\n");

  const blob = new Blob(["\uFEFF" + csvContent], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const dateStr = new Date().toISOString().slice(0, 10);
  a.download = `wallet-report-${dateStr}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function WalletReportsPage() {
  // Default to last 30 days - use useMemo to prevent infinite loops
  const defaultFilters = useMemo(() => {
    const toDate = new Date();
    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - 30);

    return {
      fromDate,
      toDate,
    };
  }, []);

  const [filters, setFilters] = useState<WalletParams>(defaultFilters);
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);
  const [isExporting, setIsExporting] = useState(false);

  const transactionsParams: WalletTransactionsParams = useMemo(
    () => ({
      ...filters,
      limit,
      offset,
    }),
    [filters, limit, offset],
  );

  const { data: summaryData, isLoading: isLoadingSummary } =
    useWalletSummaryQuery(filters);
  const { data: transactionsData, isLoading: isLoadingTransactions } =
    useWalletTransactionsQuery(transactionsParams);

  const handlePaginationChange = (newOffset: number, newLimit: number) => {
    setOffset(newOffset);
    setLimit(newLimit);
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const exportParams: WalletTransactionsParams = {
        ...filters,
        limit: 10000, // High limit for export
        offset: 0,
      };
      const result = await reportsApi.getWalletExport(exportParams);

      if (result.transactions.length === 0) {
        toast.info("ບໍ່ມີຂໍ້ມູນສົ່ງອອກ");
        return;
      }

      exportWalletToCsv(result.transactions);
      toast.success(
        `ສົ່ງອອກ ${result.transactions.length.toLocaleString("lo-LA")} ລາຍການແລ້ວ`,
      );
    } catch (error) {
      console.error("Export error:", error);
      toast.error("ສົ່ງອອກລົ້ມເຫຼວ");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            {
              label: "ລາຍງານ Wallet",
              to: "/app/reports/wallet",
            },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="flex items-center gap-2 font-bold text-2xl tracking-tight">
              <WalletIcon className="size-5" />
              ລາຍງານ Wallet
            </h2>
            <p className="text-muted-foreground">
              ຕິດຕາມທຸລະກຳ Wallet ເຕີມເງິນ, ໃຊ້ຈ່າຍ, ຄືນເງິນ ແລະ ສະຖານະອະນຸມັດ
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={isExporting}
            className="flex items-center gap-2"
          >
            <FileDownIcon className="size-4" />
            {isExporting ? "ກຳລັງສົ່ງອອກ…" : "ສົ່ງອອກ (CSV)"}
          </Button>
        </div>

        <WalletReportsFilter filters={filters} onFiltersChange={setFilters} />

        <div className="mb-4">
          <WalletReportsSummaryCards
            totalTopup={summaryData?.totalTopup ?? 0}
            totalUsage={summaryData?.totalUsage ?? 0}
            totalRefund={summaryData?.totalRefund ?? 0}
            totalTransactions={summaryData?.totalTransactions ?? 0}
            topCustomerName={summaryData?.topCustomerName ?? null}
            isLoading={isLoadingSummary}
          />
        </div>

        <WalletReportsTable
          data={transactionsData?.items ?? []}
          isLoading={isLoadingTransactions}
          totalCount={transactionsData?.total ?? 0}
          limit={limit}
          offset={offset}
          onPaginationChange={handlePaginationChange}
        />
      </Main>
    </>
  );
}
